import * as React from 'react';
import { connectStore } from '../../appContext/StoreProvider/ConnectStore';
import { FeedbackButton } from './FeedbackButton';
import { fromRelativeUrlToAbsoluteUrl } from '../../../utils/locationUtils';
import { sendGiveFeedbackClickEvent } from '../../LoggerFactory/BiAnalyticsLogger/events/utils';
import { LoggerFactory } from '../../LoggerFactory';
import { withLogger } from '../../appContext/LoggerProvider';
import window from 'global';
import { LazyLoader } from '../LazyLoader';
import Experiments from '@wix/wix-experiments';
import { withExperiments } from '@wix/wix-experiments-react';
import { httpClient } from '../../../common/utils/httpClient';
import { ClientStore } from '../../../Store/StoresObjects/ClientStore';
import { isClientSide } from '../../../common/utils';

const FeedbackModal = React.lazy(() => import('./FeedbackModal'));

interface FeedbackProps {
  memberId: string;
  store: ClientStore;
  experiments: Experiments;
  logger: LoggerFactory;
}

interface FeedbackState {
  modalOpen: boolean;
  text?: string;
  like?: any;
  email?: string;
  isEmailValid: boolean;
  submittedLike?: boolean;
  submittedUnlike?: boolean;
}

class Feedback extends React.Component<FeedbackProps, FeedbackState> {
  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      isEmailValid: true,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleLikeClick = this.handleLikeClick.bind(this);
    this.handleUnlikeClick = this.handleUnlikeClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleBlurEmail = this.handleBlurEmail.bind(this);
  }

  getMemberIdWithDots = (memberId) => memberId.replace(/\//g, '.');

  getRelativePathnameFromMemberId = (memberId) => `/${memberId}`;

  getArticleUrlFromMemberId = (memberId) => {
    const relativePathname = this.getRelativePathnameFromMemberId(memberId);
    return fromRelativeUrlToAbsoluteUrl(
      relativePathname,
      window.location.origin,
      this.props.store,
    );
  };

  getArticleNameFromMemberId = (memberId) => {
    const articleParts = memberId.split('/').splice(1);
    return articleParts.join('.');
  };

  handleSubmit() {
    const memberIdWithDots = this.getMemberIdWithDots(this.props.memberId);
    const articleUrl = this.getArticleUrlFromMemberId(this.props.memberId);
    const articleName = this.getArticleNameFromMemberId(this.props.memberId);

    httpClient.post(
      `${window.location.origin}/_serverless/wix-docs-feedback-api/feedback`,
      {
        text: this.state.text,
        like: this.state.like,
        viewerId: this.props.store.viewer.artifactId,
        memberId: memberIdWithDots,
        email: this.state.email,
        articleUrl,
        articleName,
      },
    );

    this.setState({
      modalOpen: false,
    });

    if (this.state.like) {
      this.setState({
        submittedLike: true,
        submittedUnlike: null,
      });
    } else {
      this.setState({
        submittedLike: null,
        submittedUnlike: true,
      });
    }
  }

  handleTextChange(ev) {
    this.setState({
      text: ev.target.value,
    });
  }

  handleLikeClick() {
    this.setState({
      like: true,
      modalOpen: true,
    });

    this.sendGiveFeedbackEvent(true);
  }

  handleUnlikeClick() {
    this.setState({
      like: false,
      modalOpen: true,
    });
    this.sendGiveFeedbackEvent(false);
  }

  sendGiveFeedbackEvent = (positiveFeedback) => {
    const { logger } = this.props;
    const articleUrl = this.getArticleUrlFromMemberId(this.props.memberId);
    const articleName = this.getArticleNameFromMemberId(this.props.memberId);
    sendGiveFeedbackClickEvent(
      logger,
      articleName,
      articleUrl,
      positiveFeedback,
    );
  };

  handleClose() {
    this.setState({
      modalOpen: false,
      isEmailValid: true,
    });
  }

  handleEmailChange(ev) {
    this.setState({
      email: ev.target.value,
    });
  }

  handleBlurEmail() {
    const hasEmail = !!this.state.email;
    const isValid = hasEmail
      ? /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(this.state.email)
      : true;
    this.setState({ isEmailValid: isValid });
  }

  render() {
    return (
      <>
        <FeedbackButton
          onLikeClick={this.handleLikeClick}
          onUnlikeClick={this.handleUnlikeClick}
          submittedLike={this.state.submittedLike}
          submittedUnlike={this.state.submittedUnlike}
        />
        {isClientSide() && this.state.modalOpen && (
          <LazyLoader
            lazyComponent={FeedbackModal}
            like={this.state.like}
            onSubmit={this.handleSubmit}
            onTextChange={this.handleTextChange}
            onEmailChange={this.handleEmailChange}
            onBlurEmail={this.handleBlurEmail}
            onClose={this.handleClose}
            isEmailValid={this.state.isEmailValid}
          />
        )}
      </>
    );
  }
}

export const FeedbackContainer = withLogger(
  withExperiments(connectStore(Feedback)),
);
